// F3 — las URLs de salida del buscador.
//
// Aquí NO se navega: cada función devuelve una cadena. Así se pueden comparar contra las URLs
// golden de F0 (`docs/buscador-embebible-renfeviajes-f0-urls.md`) sin lanzar una compra, que
// es la única forma de saber que la reescritura no se ha desviado.
//
// Hay SEIS formas de URL y no se parecen entre sí ni en el nombre de los parámetros ni en el
// formato de las fechas. Conviven tres formatos de fecha:
//
//   DD-MM-YYYY   dateFrom / dateTo            (rama de tren + hotel y la de Viaje+)
//   DD/MM/YYYY   _paramFrom / _paramTo        (rama _param…)
//   YYYY-MM-DD   dentro del JSON de `data`    (rama de Viaje+)
//
// DOS AVISOS PARA QUIEN TOQUE ESTO:
//
// 1. Los valores NO se escapan, a propósito. La home concatena `JSON.stringify(rooms)` en la
//    URL tal cual, así que los `[`, `{` y `"` viajan sin codificar y así los espera el otro
//    lado. Pasar esto por URLSearchParams cambiaría todas las URLs de golpe.
// 2. El ORDEN de los parámetros es el de la home, literal. No se toca: las URLs golden se
//    comparan como cadenas.
import { formatearGuiones, formatearIso, sumarDias, mismoDia } from './fechas.js'

/**
 * Las dos vistas del proceso de compra de destino.
 *
 * `CompraDestinoNew` es el rediseno: otra ruta, otro controlador y otro bundle
 * (`/:lang/CompraDestinoNew/:idProducto`). Hoy la eleccion vive escrita a mano en el JS de cada
 * portada --la de TrenMasHotel lleva `agentesCompraDestinoNew = ['581296', '711951', ...]` y mira
 * el id que pinta BloquePie en `#agenteIdMarcaBlanca`-- y la de renfeviajes, que es la que
 * replica este widget, va siempre a la clasica.
 *
 * Aqui se pide con `data-compra-destino="nuevo"`, y por eso la lista de agentes no se copia: una
 * landing sabe a que proceso quiere entrar mejor que una lista dentro del bundle, y el dia que
 * cambie no hay que volver a compilar el widget.
 */
export const COMPRA_DESTINO = 'CompraDestino'
export const COMPRA_DESTINO_NUEVO = 'CompraDestinoNew'

/**
 * Que vista pide la landing. Sin decir nada, la de siempre.
 *
 * Se aceptan los dos nombres --el del papel (`nuevo`) y el de la ruta (`CompraDestinoNew`)--
 * porque quien escribe el snippet puede venir de cualquiera de los dos sitios. Lo que no se
 * reconozca cae a la clasica con un aviso: una landing mal escrita no puede mandar al cliente a
 * una ruta que no existe.
 */
export function rutaCompraDestino (pedida) {
  const valor = String(pedida || '').trim().toLowerCase()
  if (!valor || valor === 'clasico' || valor === 'clasica' || valor === COMPRA_DESTINO.toLowerCase()) {
    return COMPRA_DESTINO
  }
  if (valor === 'nuevo' || valor === 'nueva' || valor === 'new' || valor === COMPRA_DESTINO_NUEVO.toLowerCase()) {
    return COMPRA_DESTINO_NUEVO
  }
  console.warn('[buscador] data-compra-destino no reconocido: ' + pedida + ' (se usa ' + COMPRA_DESTINO + ')')
  return COMPRA_DESTINO
}

/** El id de producto de tren + hotel. En la home es fijo (Config renfe/idProductoTrenHotel). */
export const PRODUCTO_TREN_HOTEL = 'Compra/3833'
/** El de la pestaña Hotel cuando el destino no es un hotel concreto. */
export const PRODUCTO_HOTEL = 'Compra/8679'
/** A dónde va un destino internacional. En la home es una ruta RELATIVA, sin idioma delante. */
export const DESTINO_INTERNACIONAL = 'Modelos/6077'

export const AVISO_INTERNACIONAL =
  'Debe seleccionar una estación internacional, tanto de origen como de destino / '
  + 'You must select an international origin station and international destination station'

/**
 * Las dos fechas, en los dos formatos. Réplica de `dates()`.
 *
 * En sólo ida la home no deja la vuelta vacía: **le suma un día a la ida**. Por eso una
 * búsqueda de sólo ida sigue llevando `dateTo`.
 */
export function fechasDe (estado) {
  const ida = estado.fechaIda
  if (estado.soloIda || !estado.fechaVuelta) {
    const vuelta = sumarDias(ida, 1)
    return {
      from: formatearGuiones(ida), to: formatearGuiones(vuelta),
      fromOcio: formatearGuiones(ida).replace(/-/g, '/'),
      toOcio: formatearGuiones(vuelta).replace(/-/g, '/'),
    }
  }
  return {
    from: formatearGuiones(ida), to: formatearGuiones(estado.fechaVuelta),
    fromOcio: formatearGuiones(ida).replace(/-/g, '/'),
    toOcio: formatearGuiones(estado.fechaVuelta).replace(/-/g, '/'),
  }
}

/**
 * Las habitaciones, tal y como viajan en `rooms`.
 * `childrenCount` SUMA niños y bebés, aunque fuera vayan en parámetros separados.
 */
export function habitacionesDe (pasajeros) {
  if (!pasajeros || !Array.isArray(pasajeros.porHabitacion) || !pasajeros.porHabitacion.length) {
    // Lo que hace la home cuando no hay ninguna habitación montada: tira de la que trae el
    // markup por defecto, que son 2 adultos.
    return [{ adultsCount: 2, childrenCount: 0, childrenAges: [] }]
  }
  return pasajeros.porHabitacion
}

function cuentas (pasajeros) {
  return {
    adultos: pasajeros ? pasajeros.adultos : 2,
    ninos: pasajeros ? pasajeros.ninos : 0,
    bebes: pasajeros ? pasajeros.bebes : 0,
    habitaciones: pasajeros ? pasajeros.habitaciones : 1,
  }
}

/**
 * Rama A y C: tren + hotel. `Compra/3833`.
 *
 * @param {object} opciones
 * @param {Date} [opciones.fechaSalidaHotel]  la que elige el calendario extra de sólo ida
 */
export function urlTrenHotel (datos, estado, opciones = {}) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  let vuelta = f.to

  // En sólo ida, la salida del hotel manda sobre la vuelta del tren (que no existe).
  if (estado.soloIda && opciones.fechaSalidaHotel) vuelta = formatearGuiones(opciones.fechaSalidaHotel)
  // Y si las dos fechas coinciden, la home empuja la vuelta un día: un hotel de cero noches
  // no se puede reservar.
  else if (f.to === f.from) vuelta = formatearGuiones(sumarDias(estado.fechaIda, 1))

  return datos.url(PRODUCTO_TREN_HOTEL)
    + '?dateFrom=' + f.from
    + '&dateTo=' + vuelta
    + '&departureStation=' + estado.origen.id
    + '&destinationStation=' + estado.destino.id
    + '&numTicketsAdults=' + c.adultos
    + '&numTicketsChildren=' + c.ninos
    + '&numTicketsBabies=' + c.bebes
    + '&roomCount=' + c.habitaciones
    + '&rooms=' + JSON.stringify(habitacionesDe(estado.pasajeros))
    + (estado.soloIda ? '&soloIda=true' : '')
    + '&noautoload=false&showPriceHotel=true'
}

/** Los dos productos del buscador de tren + coche (la página 656, `lanzarBusqueda`). */
export const PRODUCTO_TREN_COCHE = 'Compra/5996'
export const PRODUCTO_TREN_HOTEL_COCHE = 'Compra/5997'

/** La edad del conductor que se da por buena si no llega ninguna: la primera del selector. */
export const EDAD_CONDUCTOR_POR_DEFECTO = 21

function edadConductorDe (pasajeros) {
  const edad = Number(pasajeros && pasajeros.edadConductor)
  return Number.isInteger(edad) && edad > 0 ? edad : EDAD_CONDUCTOR_POR_DEFECTO
}

/**
 * Tren + hotel + coche: `Compra/5997`. Es la de tren + hotel con `driverAge` y los dos
 * `showPrice…` de más, en el orden en que los pone la página de coches.
 *
 * @param {object} opciones
 * @param {Date} [opciones.fechaSalidaHotel]  la que elige el calendario extra de sólo ida
 */
export function urlTrenHotelCoche (datos, estado, opciones = {}) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  let vuelta = f.to
  if (estado.soloIda && opciones.fechaSalidaHotel) vuelta = formatearGuiones(opciones.fechaSalidaHotel)
  else if (f.to === f.from) vuelta = formatearGuiones(sumarDias(estado.fechaIda, 1))

  return datos.url(PRODUCTO_TREN_HOTEL_COCHE)
    + '?dateFrom=' + f.from
    + '&dateTo=' + vuelta
    + '&departureStation=' + estado.origen.id
    + '&destinationStation=' + estado.destino.id
    + '&numTicketsAdults=' + c.adultos
    + '&numTicketsChildren=' + c.ninos
    + '&numTicketsBabies=' + c.bebes
    + '&roomCount=' + c.habitaciones
    + '&rooms=' + JSON.stringify(habitacionesDe(estado.pasajeros))
    + '&driverAge=' + edadConductorDe(estado.pasajeros)
    + (estado.soloIda ? '&soloIda=true' : '')
    + '&noautoload=false&showPriceHotel=true&showPriceTrain=true&allTrains=true'
}

/**
 * Tren + coche, sin hotel: `Compra/5996`. Sin habitaciones.
 *
 * @param {{recogida: Date, devolucion: Date}} [fechasCoche]  en sólo ida, las que elige el
 *        cliente para el coche. Viajan como `carDateFrom` / `carDateTo`; en ida y vuelta no van,
 *        porque el coche se recoge y se devuelve con los trenes.
 */
export function urlTrenCoche (datos, estado, fechasCoche) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  const conFechasCoche = estado.soloIda && fechasCoche && fechasCoche.recogida && fechasCoche.devolucion

  return datos.url(PRODUCTO_TREN_COCHE)
    + '?dateFrom=' + f.from
    + '&dateTo=' + f.to
    + '&departureStation=' + estado.origen.id
    + '&destinationStation=' + estado.destino.id
    + '&numTicketsAdults=' + c.adultos
    + '&numTicketsChildren=' + c.ninos
    + '&numTicketsBabies=' + c.bebes
    + '&driverAge=' + edadConductorDe(estado.pasajeros)
    + (estado.soloIda ? '&soloIda=true' : '')
    + (conFechasCoche
      ? '&carDateFrom=' + formatearGuiones(fechasCoche.recogida) + '&carDateTo=' + formatearGuiones(fechasCoche.devolucion)
      : '')
    + '&noautoload=false&showPriceTrain=true&allTrains=true'
}

/**
 * Rama de la pestaña Hotel. Dos variantes según el destino sea un hotel concreto o una ciudad.
 */
export function urlHotel (datos, estado) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  const destino = estado.destino
  const esHotel = destino.tipo === 'Hotel'
  const ruta = esHotel ? destino.urlFichas : PRODUCTO_HOTEL

  return datos.url(ruta)
    + '?dateFrom=' + f.from
    + '&dateTo=' + f.to
    + '&roomCount=' + c.habitaciones
    + '&rooms=' + JSON.stringify(habitacionesDe(estado.pasajeros))
    + (esHotel ? '&idDestino=' + destino.idCity + '&idHoteles=' + destino.id
      : '&idDestino=' + destino.id)
    + '&noautoload=false&autoOpenHotel=true&showPriceHotel=true'
}

/**
 * Rama B: ocio CON paquete Viaje+. `CompraDestino/{idPaquete}`.
 *
 * OJO con el `idPaquete`: la home hace `viajePlus !== null ? [..] : 0` y luego lee `[0]`. Si la
 * llamada responde `null` —que no es lo mismo que fallar— el índice se aplica sobre el número
 * 0 y sale `undefined`, así que navega a `CompraDestino/undefined`. Se replica, porque la
 * decisión de la rama es replicar los bugs; queda anotado en el doc de F3.
 */
export function urlViajePlus (datos, estado, viajePlus, ruta) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  const partes = viajePlus !== null && viajePlus !== undefined
    ? [viajePlus.idPaquete, viajePlus.idEntrada, viajePlus.idModelo, viajePlus.idDestino]
    : 0

  return datos.url(ruta || COMPRA_DESTINO) + '/' + partes[0]
    + '?dateFrom=' + f.from
    + '&dateTo=' + f.to
    + '&departureStation=' + estado.origen.id
    + '&destinationStation=' + estado.destino.id
    + '&numTicketsAdults=' + c.adultos
    + '&numTicketsChildren=' + c.ninos
    + '&numTicketsBabies=' + c.bebes
    + '&soloIda=' + (estado.soloIda ? 'true' : 'false')
    // La fecha de dentro de `data` va del revés que dateFrom: YYYY-MM-DD.
    // Codificado: sin codificar, la URL se cortaba en el primer `[` al reabrirla en el móvil y
    // llegaba `data=` vacío, sin la entrada (venta 1327273). La compra lo lee con $route.query,
    // que lo descodifica igual.
    + '&data=' + encodeURIComponent('[{"date":"' + formatearIso(estado.fechaIda) + '"},{"selects":[{"'
      + (partes[2]) + '":"' + c.adultos + '"}]}]')
    + '&showPriceTrain=true'
    + '&allTrains=true'
    + '&selectHideOcio=true'
    + '&destino=' + (partes[3])
}

/**
 * Rama D y el fallback de la B: la forma `_param…`, que va contra la landing del destino.
 *
 * Aquí cambia todo respecto a las demás: el prefijo de los parámetros, el formato de fecha
 * (barras) y que el objeto ENTERO de cada estación viaja serializado además de su id y su
 * código.
 *
 * @param {object} opciones
 * @param {boolean} [opciones.conHabitaciones]  la rama D manda roomCount y rooms; el fallback
 *                                              de Viaje+ no
 */
export function urlParam (datos, estado, searchType, opciones = {}) {
  const f = fechasDe(estado)
  const c = cuentas(estado.pasajeros)
  const o = estado.origen
  const d = estado.destino

  let url = datos.url(d.url)
    + '?_paramFrom=' + f.fromOcio
    + '&_paramTo=' + f.toOcio
    + '&_paramType=' + searchType
    + '&_paramOrigin=' + o.id
    + '&_paramOriginCode=' + o.codigoestacionrenfe
    + '&_paramOriginObject=' + JSON.stringify(o)
    + '&_paramDestination=' + d.id
    + '&_paramDestinationCode=' + d.codigoestacionrenfe
    + '&_paramDestinationObject=' + JSON.stringify(d)
    + '&_paramNumAdults=' + c.adultos
    + '&_paramNumChildren=' + c.ninos
    + '&_paramNumBabies=' + c.bebes

  if (opciones.conHabitaciones) {
    // El orden importa: en la home `_paramTag` va ANTES que roomCount en esta rama y DESPUÉS
    // en la otra. No es un descuido de este fichero, es lo que hay.
    url += '&_paramTag=todos'
      + '&_paramRoomCount=' + opciones.roomCount
      + '&_paramRooms=' + JSON.stringify(habitacionesDe(estado.pasajeros))
      + (estado.soloIda ? '&_paramSoloIda=true' : '')
      + '&_paramOrden=1'
  } else {
    url += (estado.soloIda ? '&_paramSoloIda=true' : '')
      + '&_paramTag=todos'
      + '&_paramOrden=1'
  }
  return url
}

/**
 * La regla internacional, que se mira ANTES de construir nada.
 * @returns {{corta: boolean, aviso?: string, url?: string}}
 */
export function reglaInternacional (datos, estado) {
  const o = estado.origen || {}
  const d = estado.destino || {}
  const oInt = String(o.isinternacional) === '1'
  const dInt = String(d.isinternacional) === '1'

  if (oInt || dInt) {
    // Una sola de las dos internacional: no se puede viajar así, y la home lo dice con un
    // alert bilingüe.
    if (!oInt || !dInt) return { corta: true, aviso: AVISO_INTERNACIONAL }
  }
  if (dInt) return { corta: true, url: DESTINO_INTERNACIONAL }
  return { corta: false }
}
